import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/components/ui/cx';
import { type ShipmentDetail, shipmentKeys, shipmentsApi } from '@/lib/api/shipments';
import { clockTime, dayMonthTime, relative } from '@/lib/format/time';

/** «08:00» today, «ieri 16:44» yesterday, else «15/11 09:30». */
export function dayTime(date: Date, yesterday: string, now = new Date()): string {
  if (date.toDateString() === now.toDateString()) return clockTime(date);
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (date.toDateString() === y.toDateString()) return `${yesterday} ${clockTime(date)}`;
  return dayMonthTime(date);
}

import { useNow } from '@/lib/hooks/use-now';

// The header's second row (S02): «Dokless rev 42 consegnata 08:00» and
// «Prossimo controllo tra 3 h · stato: in viaggio» with the why on hover.

export function useDeliveries(id: string) {
  return useQuery({
    queryKey: shipmentKeys.part(id, 'deliveries'),
    queryFn: () => shipmentsApi.deliveries(id),
  });
}

export function RevisionLine({ shipment: s, short = false }: { shipment: ShipmentDetail; short?: boolean }) {
  const { t } = useTranslation('shipment');
  const deliveries = useDeliveries(s.id);
  const delivered = s.revision.last_delivered;
  const row =
    delivered === null
      ? undefined
      : deliveries.data?.payloads.find((p) => p.revision === delivered && p.state === 'delivered');
  const latest = deliveries.data?.payloads.find((p) => p.revision === s.revision.current);
  const at = row?.sent_at ? dayTime(new Date(row.sent_at), t('header.yesterday')) : null;
  let text: string;
  let light = 'd-ok';
  if (delivered !== null)
    text = at
      ? t(short ? 'header.revShort' : 'header.revDelivered', { rev: delivered, at })
      : t('header.revDeliveredNoTime', { rev: delivered });
  else if (latest?.state === 'shadow') {
    text = t('header.revShadow', { rev: s.revision.current });
    light = 'd-pl';
  } else if (latest && ['dead', 'rejected'].includes(latest.state)) {
    text = t('header.revFailed', { rev: s.revision.current });
    light = 'd-bl';
  } else if (s.revision.current > 0) {
    text = t('header.revPending', { rev: s.revision.current });
    light = 'd-wa';
  } else {
    text = t('header.revNone');
    light = 'd-pl';
  }
  return (
    <span className="inline-flex items-center" style={{ gap: 6 }} data-testid="revision-line">
      <span className={cx('dot', light)} />
      {text}
    </span>
  );
}

/** «status:in_transit» → «stato: in viaggio»; «user:track_now» → «chiesto da una persona». */
export function useCheckReason() {
  const { t } = useTranslation('shipment');
  const { t: tc } = useTranslation();
  return (reason: string | null): string | null => {
    if (!reason) return null;
    const [kind, value] = reason.split(':', 2);
    if (kind === 'status' && value)
      return t('next.status', { status: tc(`shipmentStatus.${value}`).toLocaleLowerCase() });
    return t(`next.reason.${kind}`, { defaultValue: reason });
  };
}

export function NextCheckLine({
  shipment: s,
  icon,
  short = false,
}: {
  shipment: ShipmentDetail;
  icon?: ReactNode;
  short?: boolean;
}) {
  const { t } = useTranslation('shipment');
  const { t: tg } = useTranslation();
  const now = useNow(30_000);
  const reason = useCheckReason()(s.next_check_reason);
  let text: string;
  if (s.removed_from_queue) text = t('next.suspended');
  else if (s.status === 'closed') text = t('next.closed');
  else if (s.status === 'blocked') text = t('next.blocked');
  else if (!s.next_check_at) text = t('next.none');
  else {
    const when = relative(new Date(s.next_check_at), new Date(now), tg);
    text =
      s.consecutive_failures > 0
        ? t(short ? 'next.retryShort' : 'next.retry', { when, count: s.consecutive_failures })
        : short || !reason
          ? t('next.at', { when })
          : t('next.atWhy', { when, why: reason });
  }
  return (
    <span
      className="inline-flex items-center"
      style={{ gap: 6 }}
      title={s.next_check_reason ?? undefined}
      data-testid="next-check"
    >
      {icon}
      {text}
    </span>
  );
}
